import React from 'react';
import { TrendingUp, TrendingDown, Activity, BarChart3, PieChart, DollarSign } from 'lucide-react';

interface MarketStatsProps {
  stats: {
    total: number;
    buy: number;
    sell: number;
    hold: number;
    avgUpside: number;
  };
}

export const MarketStats: React.FC<MarketStatsProps> = ({ stats }) => {
  const cards = [
    {
      label: 'Total Recommendations',
      value: stats.total.toLocaleString(),
      icon: <BarChart3 className="h-6 w-6" />,
      color: 'from-blue-500 to-blue-600',
      bg: 'bg-blue-50',
      textColor: 'text-blue-700',
    },
    {
      label: 'Buy Calls',
      value: stats.buy.toLocaleString(),
      icon: <TrendingUp className="h-6 w-6" />,
      color: 'from-emerald-500 to-green-600',
      bg: 'bg-green-50',
      textColor: 'text-green-700',
      badge: `${((stats.buy / stats.total) * 100).toFixed(0)}%`,
    },
    {
      label: 'Sell Calls',
      value: stats.sell.toLocaleString(),
      icon: <TrendingDown className="h-6 w-6" />,
      color: 'from-red-500 to-rose-600',
      bg: 'bg-red-50',
      textColor: 'text-red-700',
      badge: `${((stats.sell / stats.total) * 100).toFixed(0)}%`,
    },
    {
      label: 'Hold',
      value: stats.hold.toLocaleString(),
      icon: <Activity className="h-6 w-6" />,
      color: 'from-amber-500 to-orange-600',
      bg: 'bg-amber-50',
      textColor: 'text-amber-700',
      badge: `${((stats.hold / stats.total) * 100).toFixed(0)}%`,
    },
    {
      label: 'Avg Upside',
      value: `${stats.avgUpside.toFixed(2)}%`,
      icon: <PieChart className="h-6 w-6" />,
      color: 'from-purple-500 to-indigo-600',
      bg: 'bg-purple-50',
      textColor: 'text-purple-700',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {cards.map((card, index) => (
        <div
          key={index}
          className="group relative bg-white rounded-2xl p-5 border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
        >
          {/* Animated background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2.5 rounded-xl bg-gradient-to-br ${card.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <div className="text-white">{card.icon}</div>
              </div>
              {card.badge && (
                <span className={`px-2 py-1 ${card.bg} ${card.textColor} text-xs font-semibold rounded-lg`}>
                  {card.badge}
                </span>
              )}
            </div>
            
            <div>
              <p className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                {card.value}
              </p>
              <p className="text-xs text-gray-600 font-medium">{card.label}</p>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className={`absolute -right-6 -bottom-6 w-24 h-24 bg-gradient-to-br ${card.color} rounded-full opacity-5 group-hover:opacity-10 transition-opacity duration-300`} />
        </div>
      ))}
    </div>
  );
};
